import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField } from '@mui/material'
import React, { useCallback, useMemo, useRef, useState } from 'react'

import { AgGridReact } from 'ag-grid-react'
import { numberWithCommasDecimals } from 'utils/stringFunctions'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'

function flattenAlertsFromNotifications(notifications) {
  const rows = []
  if (!Array.isArray(notifications)) return rows

  for (const n of notifications) {
    const notifType = n?.type ?? ''
    const alerts = Array.isArray(n?.alerts) ? n.alerts : []

    for (const a of alerts) {
      rows.push({
        ...a,
        type: notifType,
      })
    }
  }

  return rows
}

const CopyAlertDialog = ({ open, onClose, onSubmit, alertUsers, alertRoles, alertGroups, sourceOptions }) => {
  const { getAgGridTheme } = useInnovaTheme()

  const gridApi = useRef(null)
  const [selectedSourceId, setSelectedSourceId] = useState('')
  const [sourceRowData, setSourceRowData] = useState([])
  const isRolesMode = Array.isArray(alertRoles) && alertRoles.length > 0
  const isUsersMode = !isRolesMode && Array.isArray(alertUsers) && alertUsers.length > 0
  const isGroupsMode = !isRolesMode && !isUsersMode && Array.isArray(alertGroups) && alertGroups.length > 0
  const title = isRolesMode ? 'Copy From Roles' : isUsersMode ? 'Copy From Users' : isGroupsMode ? 'Copy From Groups' : ''
  const dropdownLabel = isRolesMode ? 'Role' : isUsersMode ? 'User' : isGroupsMode ? 'Group' : ''
  const [copyMode, setCopyMode] = useState('append')

  const onGridReady = useCallback((params) => {
    gridApi.current = params.api
  }, [])

  const columnDefs = useMemo(
    () => [
      { headerName: 'Type', field: 'type', width: 160 },

      { headerName: 'Name', field: 'name', width: 220 },
      { headerName: 'Severity', field: 'severity', width: 120 },

      { headerName: 'Phase', field: 'phase', width: 180 },
      { headerName: 'Rig', field: 'rig', width: 180 },

      {
        headerName: 'Use Min',
        field: 'useMin',
        width: 110,
        valueFormatter: (p) => (p.value ? 'Yes' : 'No'),
      },
      {
        headerName: 'Min',
        field: 'min',
        width: 110,
        valueFormatter: (p) => numberWithCommasDecimals(p.value, 2),
      },

      {
        headerName: 'Use Max',
        field: 'useMax',
        width: 110,
        valueFormatter: (p) => (p.value ? 'Yes' : 'No'),
      },
      {
        headerName: 'Max',
        field: 'max',
        width: 110,
        valueFormatter: (p) => numberWithCommasDecimals(p.value, 2),
      },

      { headerName: 'Unit', field: 'unit', width: 120 },

      {
        headerName: 'Use Min Depth',
        field: 'useMinDepth',
        width: 140,
        valueFormatter: (p) => (p.value ? 'Yes' : 'No'),
      },
      {
        headerName: 'Min Depth',
        field: 'minDepth',
        width: 120,
        valueFormatter: (p) => numberWithCommasDecimals(p.value, 2),
      },

      {
        headerName: 'Use Max Depth',
        field: 'useMaxDepth',
        width: 140,
        valueFormatter: (p) => (p.value ? 'Yes' : 'No'),
      },
      {
        headerName: 'Max Depth',
        field: 'maxDepth',
        width: 120,
        valueFormatter: (p) => numberWithCommasDecimals(p.value, 2),
      },
      {
        headerName: 'Include Projections',
        field: 'ignoreProjections',
        width: 130,
        valueFormatter: (p) => (p.value ? 'Yes' : 'No'),
      },
      {
        headerName: 'Silence Alert',
        field: 'silenceAlert',
        width: 120,
        valueFormatter: (p) => (p.value ? 'Yes' : 'No'),
      },

      { headerName: 'UID', field: 'uid', width: 120, hide: true },
      {
        headerName: 'Active',
        field: 'active',
        width: 100,
        valueFormatter: (p) => (p.value ? 'Yes' : 'No'),
      },
    ],
    [],
  )

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      editable: false,
      filter: true,
    }),
    [],
  )

  const getNotificationsForSource = useCallback(
    (id) => {
      if (!id) return []

      if (isUsersMode) {
        const u = (alertUsers || []).find((x) => x?.userName === id)
        return u?.alerts?.notifications || []
      }

      // roles mode
      if (isRolesMode) {
        const r = (alertRoles || []).find((x) => (x?.id ?? `${x.organization}:${x.name}`) === id)
        return r?.alerts?.notifications || []
      }

      // groups mode
      const g = (alertGroups || []).find((x) => x?.groupName === id)
      return g?.groupDetails?.alerts?.notifications || []
    },
    [alertUsers, alertRoles, isUsersMode, isRolesMode, alertGroups],
  )

  const handleSourceChange = useCallback(
    (e) => {

      const nextId = e.target.value ?? ''
      setSelectedSourceId(nextId)

      if (!nextId) {
        setSourceRowData([])
        return
      }

      const notifications = getNotificationsForSource(nextId)
      const flat = flattenAlertsFromNotifications(notifications)
      setSourceRowData(flat)
    },
    [getNotificationsForSource],
  )

  const handleSubmit = useCallback(() => {
    if (!gridApi.current) return
    const selected = gridApi.current.getSelectedRows() || []
    onSubmit?.({ mode: copyMode, alerts: selected })
  }, [onSubmit, copyMode])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='lg'
      PaperProps={{
        sx: {
          backgroundColor: 'itemBackground',
          height: '80vh',
        },
      }}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2, overflow: 'visible' }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            pt: 1,
            overflow: 'visible',
          }}>
          <TextField
            select
            size='small'
            label={dropdownLabel}
            value={selectedSourceId}
            onChange={handleSourceChange}
            sx={{ minWidth: 320, flexShrink: 0 }}>
            <MenuItem value=''>Select {dropdownLabel}...</MenuItem>
            {(sourceOptions || []).map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size='small'
            label='Mode'
            value={copyMode}
            onChange={(e) => setCopyMode(e.target.value)}
            sx={{ minWidth: 180, flexShrink: 0 }}>
            <MenuItem value='append'>Append</MenuItem>
            <MenuItem value='overwrite'>Overwrite</MenuItem>
          </TextField>
        </Box>
        <Box sx={{ flex: 1, width: '100%' }}>
          <div className={getAgGridTheme()} style={{ width: '100%', height: '100%' }}>
            <AgGridReact
              rowData={sourceRowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowSelection={{
                mode: 'multiRow',
                checkboxes: true,
                headerCheckbox: true,
              }}
              onGridReady={onGridReady}
            />
          </div>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='secondary' onClick={onClose}>
          Cancel
        </Button>
        <Button variant='outlined' color='primary' onClick={handleSubmit}>
          Copy Selected
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CopyAlertDialog
